import React, { useState } from 'react';
import { tryVisionPhoto, type VisionTryModelResult, type VisionTryResult } from './api';

const PACKS = [
  ['', 'No pack (general rules)'],
  ['fleet_damage', 'Fleet'],
  ['equipment_condition', 'Equipment'],
  ['property_condition', 'Property'],
  ['safety_equipment', 'Safety equipment'],
] as const;

const MAX_BYTES = 2 * 1024 * 1024;

function readAsBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(new Error('Could not read the file.'));
    reader.onload = () => resolve(String(reader.result).replace(/^data:[^;]+;base64,/, ''));
    reader.readAsDataURL(file);
  });
}

function ModelColumn({ title, result }: { title: string; result: VisionTryModelResult | { skipped: string } | null }) {
  return (
    <div className="card" style={{ flex: 1, minWidth: 280 }}>
      <h3 style={{ marginTop: 0 }}>{title}</h3>
      {!result ? (
        <p className="muted">No trained candidate yet — only the live model ran.</p>
      ) : 'skipped' in result ? (
        <p className="muted">{result.skipped}</p>
      ) : (
        <>
          <p className="sub" style={{ marginBottom: 8 }}>
            <code>{result.model}</code> · {(result.ms / 1000).toFixed(1)} s{result.tokens != null ? ` · ${result.tokens} tokens` : ''}
            {result.generations && result.generations > 1 ? ` · ${result.generations} generations (first answer was malformed)` : ''}
          </p>
          {result.failed ? (
            <p className="sub">The model did not answer: {result.failed}{result.status ? ` (HTTP ${result.status})` : ''}.</p>
          ) : (result.observations ?? []).length === 0 ? (
            <p className="muted">Nothing reported for this photo.</p>
          ) : (
            <table className="matrix" style={{ width: '100%' }}>
              <thead>
                <tr>
                  <th>Finding</th>
                  <th>Kind</th>
                  <th className="col-ed">Confidence</th>
                  <th>Category · severity</th>
                  <th>Boxes</th>
                </tr>
              </thead>
              <tbody>
                {(result.observations ?? []).map((o) => (
                  <tr key={o.id}>
                    <td>{o.displayValue}{o.subject ? <div className="muted" style={{ fontSize: 11 }}>{o.subject}</div> : null}</td>
                    <td>{o.kind}{o.coverage !== 'observed' ? ` (${o.coverage})` : ''}</td>
                    <td className="col-ed">{o.confidence == null ? '—' : `${Math.round(o.confidence * 100)}%`}</td>
                    <td>{[o.defectCategory, o.severity].filter(Boolean).join(' · ') || '—'}{o.packId ? <div className="muted" style={{ fontSize: 11 }}>{o.packId}</div> : null}</td>
                    <td className="muted" style={{ fontSize: 11 }}>
                      {(o.regions ?? []).map((r, i) => <div key={i}>{r.label ?? 'box'} [{r.box.join(', ')}]</div>)}
                      {(o.regions ?? []).length === 0 ? '—' : null}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          {(result.frameQuality ?? []).length ? (
            <p className="muted" style={{ fontSize: 12, marginTop: 8 }}>Frame quality: {(result.frameQuality ?? []).map((f) => f.quality).join(', ')}</p>
          ) : null}
          {(result.guidance ?? []).length ? <p className="muted" style={{ fontSize: 12 }}>Guidance: {(result.guidance ?? []).join(' · ')}</p> : null}
        </>
      )}
    </div>
  );
}

/**
 * One photo through the live model and the latest candidate, side by side. The
 * photo is held in memory for the request only (never stored, never logged); no
 * credits are charged; 20 photos an hour per admin.
 */
export default function VisionTryPhoto({ remaining, onRan }: { remaining: number; onRan: () => void }) {
  const [file, setFile] = useState<File | null>(null);
  const [preview, setPreview] = useState<string | null>(null);
  const [pack, setPack] = useState<string>('');
  const [label, setLabel] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<VisionTryResult | null>(null);

  const pick = (f: File | null) => {
    setFile(f);
    setResult(null);
    setError(null);
    if (preview) URL.revokeObjectURL(preview);
    setPreview(f ? URL.createObjectURL(f) : null);
  };

  const run = async () => {
    if (!file) return;
    setBusy(true);
    setError(null);
    try {
      if (file.size > MAX_BYTES) throw new Error('The photo is over 2 MB; the app resizes to 2048 px before sending — do the same here.');
      const mimeType = file.type === 'image/png' ? 'image/png' : 'image/jpeg';
      const data = await readAsBase64(file);
      setResult(await tryVisionPhoto({ pack: pack || null, label, mimeType, data }));
      onRan();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="card">
      <h2 style={{ marginTop: 0 }}>Try a photo</h2>
      <p className="sub">
        Run one photo through the <strong>live</strong> model and, when one exists, the <strong>latest trained candidate</strong>, with the same prompt
        production uses. The photo stays in memory for this request only: it is not stored, not logged and never becomes training data. No credits are
        charged. {remaining} of 20 photos left this hour.
      </p>
      <div className="row" style={{ gap: 16, flexWrap: 'wrap', alignItems: 'flex-end' }}>
        <div>
          <label htmlFor="try-photo-file">Photo (JPEG or PNG, up to 2 MB)</label>
          <input id="try-photo-file" type="file" accept="image/jpeg,image/png" onChange={(e) => pick(e.target.files?.[0] ?? null)} />
        </div>
        <div>
          <label htmlFor="try-photo-pack">Pack</label>
          <select id="try-photo-pack" className="lvl" value={pack} onChange={(e) => setPack(e.target.value)}>
            {PACKS.map(([k, v]) => <option key={k} value={k}>{v}</option>)}
          </select>
        </div>
        <div style={{ flex: 1, minWidth: 220 }}>
          <label htmlFor="try-photo-label">What the photo shows (optional guidance, as a template group would give)</label>
          <input id="try-photo-label" value={label} maxLength={200} placeholder="e.g. Front bumper and wheel arch" onChange={(e) => setLabel(e.target.value)} />
        </div>
        <button className="btn primary" disabled={!file || busy || remaining <= 0} onClick={() => void run()}>
          {busy ? 'Analysing…' : 'Run both models'}
        </button>
      </div>
      {preview ? <img src={preview} alt="" style={{ maxHeight: 220, marginTop: 14, borderRadius: 8 }} /> : null}
      {error ? <p className="sub" style={{ marginTop: 12 }}>{error}</p> : null}
      {result ? (
        <div className="row" style={{ gap: 14, alignItems: 'stretch', marginTop: 14, flexWrap: 'wrap' }}>
          <ModelColumn title="Live model" result={result.production} />
          <ModelColumn title={`Latest candidate${result.candidateStatus ? ` (${result.candidateStatus.replace(/_/g, ' ')})` : ''}`} result={result.candidate} />
        </div>
      ) : null}
    </section>
  );
}
